import { NextPage } from 'next'
import { AppProps } from 'next/app'
import { AppContextType } from 'next/dist/shared/lib/utils'
import React, { ReactNode } from 'react'
import { Provider } from 'react-redux'
import { useIsomorphicLayoutEffect } from 'react-use'
import { getBogusVersion } from '@/api/get-bogus-version'
import { getEnterpriseIdentity } from '@/api/get-enterprise-identity'
import { bogusTokenService } from '@/api/utils/bogus-token-service'
import { EnterpriseIdentityProvider } from '@/components/common/contexts/enterprise-identity-context'
import AppLayout from '@/components/layout/app-layout'
import { OPEN_GRAPH_PAGES, Page } from '@/hooks/use-authorization/constants'
import getPage from '@/hooks/use-authorization/get-page'
import { getLanguageCode, getMessagesByLocale } from '@/hooks/use-i18n'
import sourceLocale from '@/i18n/locales/zh/locale.json'
import { initializeMswServerMock, MswProvider } from '@/mocks/msw-provider'
import { initStore } from '@/store/store'
import { Nullable } from '@/types/nullable'
import { EnterpriseIdentity } from '@/types/schema/api/enterprise-identity'

type NextLayoutPage<P = NonNullable<unknown>> = NextPage<P> & {
  getLayout?: (page: ReactNode, pageProps?: PageProps) => ReactNode
}

export interface PageProps {
  pathname: string
  messages: Record<string, string>
  enterpriseIdentity: Nullable<EnterpriseIdentity>
  bogusVersion: string
}

type Props = AppProps<PageProps> & {
  Component: NextLayoutPage
}

export const HomeApp = ({ Component, pageProps }: Props): ReactNode => {
  const store = initStore()

  // 確保在 client 設定了 bogus version 後才開始渲染 UI
  useIsomorphicLayoutEffect(() => {
    if (pageProps.bogusVersion) {
      bogusTokenService.init(pageProps.bogusVersion)
      // For debugging
      window.__BOGUS_VERSION__ = pageProps.bogusVersion
    }
  }, [pageProps.bogusVersion])

  return (
    <MswProvider>
      <Provider store={store}>
        <EnterpriseIdentityProvider
          enterpriseIdentity={pageProps.enterpriseIdentity}
        >
          <AppLayout Component={Component} pageProps={pageProps} />
        </EnterpriseIdentityProvider>
      </Provider>
    </MswProvider>
  )
}

HomeApp.getInitialProps = async (
  context: AppContextType,
): Promise<{
  pageProps: PageProps
}> => {
  await initializeMswServerMock()
  const enterpriseIdentity = await getEnterpriseIdentity(context.ctx.req)
  // Server side only
  const bogusVersion = context.ctx.req ? await getBogusVersion() : ''

  if (typeof context.router.query.lang !== 'string') {
    return {
      pageProps: {
        pathname: context.router.pathname,
        messages: sourceLocale,
        enterpriseIdentity,
        bogusVersion,
      },
    }
  }

  const locale = getLanguageCode(context.router.query.lang)
  const messages = OPEN_GRAPH_PAGES.includes(
    getPage(context.router.pathname) ?? Page.Default,
  )
    ? await getMessagesByLocale(locale)
    : sourceLocale

  return {
    pageProps: {
      pathname: context.router.pathname,
      messages,
      enterpriseIdentity,
      bogusVersion,
    },
  }
}

export default HomeApp
